/**
 * ModifierKeys can be used to track the state of ModifierKeys, which are SHIFT, CTRL and ALT.
 */
export class ModifierKeys {
    /**
     * ModifierKey-object with no active ModifierKeys.
     */
    public static get NONE(): ModifierKeys {
        return new ModifierKeys(0);
    }

    /**
     * ModifierKey-object with only Shift-key active.
     */
    public static get SHIFT(): ModifierKeys {
        return new ModifierKeys(this.SHIFT_BIT);
    }

    /**
     * ModifierKey-object with only Ctrl-key active.
     */
    public static get CTRL(): ModifierKeys {
        return new ModifierKeys(this.CTRL_BIT);
    }

    /**
     * ModifierKey-object with only Alt-key active.
     */
    public static get ALT(): ModifierKeys {
        return new ModifierKeys(this.ALT_BIT);
    }

    private static readonly SHIFT_BIT = 0b001;
    private static readonly CTRL_BIT = 0b010;
    private static readonly ALT_BIT = 0b100;

    //bit flag containing the state of CTRL, ALT and SHIFT ModifierKeys
    public readonly value: number;

    /**
     * Private constructor to convert number (bit-flag) to ModifierKeys-object.
     * ModifierKey-object should be constructed using the public static getModifierKeys(event)-method.
     * Or by using one of the getters; ModifierKeys.NONE, ModifierKeys.CTRL, ModifierKeys.ALT or ModifierKeys.SHIFT
     * @param value The bitflag presenting the state of the ModifierKeys.
     */
    private constructor(value: number) {
        this.value = value;
    }

    /**
     * Static method to get the state of modifierKeys from a MouseEvent.
     * @param event The mouse event from which the ModifierKey states are extracted
     * @returns ModifierKeys object presenting the state of Modifier keys in the provided event
     */
    public static getModifierKeys(event: KeyboardEvent): ModifierKeys {
        const value =
            ((event.altKey ? 1 : 0) << 2) |
            ((event.ctrlKey ? 1 : 0) << 1) |
            (event.shiftKey ? 1 : 0);
        return new ModifierKeys(value);
    }

    /**
     * Method to see if the Shift-key is active. Other ModifierKeys may be active as well.
     * @returns True if Shift-key is active. False otherwise.
     */
    public get hasShift(): boolean {
        return (this.value & ModifierKeys.SHIFT_BIT) == ModifierKeys.SHIFT_BIT;
    }

    /**
     * Method to see if the Ctrl-key is active. Other ModifierKeys may be active as well.
     * @returns True if Ctrl-key is active. False otherwise.
     */
    public get hasCtrl(): boolean {
        return (this.value & ModifierKeys.CTRL_BIT) == ModifierKeys.CTRL_BIT;
    }

    /**
     * Method to see if the Alt-key is active. Other ModifierKeys may be active as well.
     * @returns True if Alt-key is active. False otherwise.
     */
    public get hasAlt(): boolean {
        return (this.value & ModifierKeys.ALT_BIT) == ModifierKeys.ALT_BIT;
    }

    /**
     * Method to see if two ModifierKey flags are equal.
     * CTRL, ALT and SHIFT keys must all be in equal state.
     * @param ref ModifierKeys to check for equality
     * @returns true if the state of CTRL, ALT and SHIFT are all equal between the two objects
     */
    public equals(ref: ModifierKeys): boolean {
        return this.value === ref.value;
    }
}
